'use client';

import {useSignOut} from '@hooks/useSignOut';
import {Button} from '@lib/components/ui/button';
import LogoIcon from '@public/images/logo/icon.png';
import Image from 'next/image';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';
import {useEffect} from 'react';

import {LoginError} from './_lib/errors';

const LoginErrorPage = ({error, reset}: {error: LoginError | Error; reset: () => void}) => {
  const message = error instanceof LoginError ? error.data?.message : `Something went wrong`;
  const recoverable = error instanceof LoginError ? error.data?.isRecoverable : false;
  const recoveryMessage = error instanceof LoginError ? error.data?.recoveryMessage : undefined;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const signOut = useSignOut();
  const errorParam = searchParams?.get(`error`);

  useEffect(() => {
    if (!errorParam) {
      // Rerender login UI
      reset();
    }
  }, [errorParam, reset]);

  const handleReset = () => {
    if (pathname) {
      // Remove the error param from the URL
      router.replace(pathname);
    }
  };

  return (
    <div className="flex min-h-[180px] w-full flex-col items-center justify-center gap-3 text-center">
      <Image src={LogoIcon} alt="Linx" width={40} height={40} priority />
      <div className="flex flex-col items-center gap-1">
        <p className="text-header-sm font-semibold text-foreground">{message}</p>
        {recoveryMessage && <p className="text-md text-muted-foreground">{recoveryMessage}</p>}
      </div>
      <div className="flex flex-col items-center gap-2">
        {recoverable && (
          <Button variant="primary" onClick={handleReset}>
            Try again
          </Button>
        )}
        <Button variant="outline" onClick={signOut}>
          Sign out
        </Button>
      </div>
    </div>
  );
};

export default LoginErrorPage;
